// @ts-nocheck
/**
 * バトルエンジン本体
 * RPGバトルの全体管理を担当
 * @module twinKey/core/battle/BattleEngine
 */

import { TurnManager } from './TurnManager';
import { ActionExecutor } from './ActionExecutor';
import { BattleAI } from './BattleAI';
import { SKILL_3_INITIAL_CD } from '../../data/constants';

// Phase 2 機能システム
import { statusEffectEngine } from '../../features/statusEffects';
import { buffManager } from '../../features/buffs';
import { passiveManager } from '../../features/passives';
import { equipmentManager } from '../../features/equipment';

// ============================================================
// バトル状態定数
// ============================================================

export const BATTLE_STATUS = {
  PREPARING: 'preparing',
  ONGOING: 'ongoing',
  VICTORY: 'victory',
  DEFEAT: 'defeat',
  ESCAPED: 'escaped',
};

// ============================================================
// BattleEngine クラス
// ============================================================

export class BattleEngine {
  /**
   * @param {Array} allies - 味方モンスター配列
   * @param {Array} enemies - 敵配列
   * @param {Object} options - オプション
   */
  constructor(allies, enemies, options = {}) {
    // バトル状態の初期化
    this.state = {
      battleId: `battle_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      floorNumber: options.floorNumber || 1,
      battleType: options.battleType || 'normal', // 'normal' | 'strong' | 'boss'

      // 参加者
      allies: allies.map((m, i) => this.createBattleUnit(m, i, true)),
      enemies: enemies.map((e, i) => this.createBattleUnit(e, i, false)),

      // ターン管理
      turnOrder: [],
      currentTurnIndex: 0,
      turnCount: 1,

      // 状態
      status: BATTLE_STATUS.ONGOING,

      // 設定
      isAuto: options.isAuto ?? true,
      speed: options.speed ?? 1, // 1, 2, 3

      // ログ
      logs: [],

      // 統計
      stats: {
        totalDamageDealt: 0,
        totalDamageReceived: 0,
        turnsElapsed: 0,
        skillsUsed: 0,
        criticalHits: 0,
      },
    };

    // サブシステム初期化
    this.turnManager = new TurnManager(this.state);
    this.actionExecutor = new ActionExecutor(this.state);
    this.ai = new BattleAI(this.state);

    // 初期行動順決定
    this.state.turnOrder = this.turnManager.calculateTurnOrder();

    // オンチェンジコールバック
    this.onChange = options.onChange || null;

    // Phase 2: 戦闘開始時パッシブを処理
    this.processBattleStartPassives();
  }

  /**
   * バトルユニットを作成
   * @param {Object} monster - モンスター情報
   * @param {number} index - インデックス
   * @param {boolean} isAlly - 味方フラグ
   * @returns {Object} BattleUnit
   */
  createBattleUnit(monster, index, isAlly) {
    const skills = (monster.skills || []).map(s => ({
      ...s,
      currentCd: s.id === 'skill_3' ? SKILL_3_INITIAL_CD : 0,
    }));

    // 基礎ステータス
    const baseStats = {
      attack: monster.stats?.attack || monster.attack || 100,
      maxHp: monster.stats?.maxHp || monster.hp || 100,
      defense: monster.stats?.defense || 0,
      speed: monster.stats?.speed || 50,
      critRate: monster.stats?.critRate || 0.05,
      critDmg: monster.stats?.critDmg || 1.5,
    };

    // 装備補正を計算（味方のみ）
    let equipMods = { atkFlat: 0, atkPercent: 0, hpFlat: 0, hpPercent: 0, defFlat: 0, spdFlat: 0, critRate: 0, critDmg: 0 };
    if (isAlly && monster.equipment) {
      equipMods = equipmentManager.calculateEquipmentModifiers(monster);
    }

    // パッシブ補正を計算
    const passiveMods = passiveManager.calculateStatModifiers(monster, isAlly ? this.state?.allies : []);

    // 最終ステータス計算
    const finalAttack = Math.floor(
      (baseStats.attack + equipMods.atkFlat) *
      (1 + equipMods.atkPercent + (passiveMods.atkPercent || 0))
    );
    const finalMaxHp = Math.floor(
      (baseStats.maxHp + equipMods.hpFlat) *
      (1 + equipMods.hpPercent + (passiveMods.hpPercent || 0))
    );
    const finalDefense = baseStats.defense + equipMods.defFlat + (passiveMods.defFlat || 0);
    const finalSpeed = baseStats.speed + equipMods.spdFlat + (passiveMods.spdFlat || 0);
    const finalCritRate = baseStats.critRate + equipMods.critRate + (passiveMods.critRate || 0);
    const finalCritDmg = baseStats.critDmg + equipMods.critDmg;

    // パッシブ使用回数リセット
    const resetMonster = passiveManager.resetPassiveUsage(monster);

    return {
      id: isAlly ? `ally_${index}` : `enemy_${index}`,
      instanceId: monster.uniqueId || monster.cardId || `${monster.name}_${index}`,
      name: monster.name,
      isAlly,
      attribute: monster.attribute || 'なし',
      tier: monster.tier || 'D',
      awakening: monster.awakening || 0,

      // 基礎ステータス（補正前）
      baseStats,

      // 最終ステータス
      stats: {
        attack: finalAttack,
        maxHp: finalMaxHp,
        // 現在HPを保持（パーティから引き継ぐ、ただしmaxHpを超えないよう調整）
        currentHp: Math.min(monster.stats?.currentHp ?? finalMaxHp, finalMaxHp),
        defense: finalDefense,
        speed: finalSpeed,
        critRate: finalCritRate,
        critDmg: finalCritDmg,
      },

      // Phase 2: 状態異常・バフ
      statusEffects: [],
      buffs: [],

      // Phase 2: パッシブ
      passives: monster.passives || [],
      passiveUsage: resetMonster.passiveUsage || {},

      // Phase 2: 装備
      equipment: monster.equipment || {},

      skills,
      isAlive: true,
      isDefending: false,

      // 特殊状態
      shield: 0,           // シールド量
      invincibleTurns: 0,  // 無敵ターン数
      hasGuts: false,      // 根性（1回きり）

      // 元データ参照（経験値付与等に使用）
      sourceData: monster,
    };
  }

  // ============================================================
  // バトル実行
  // ============================================================

  /**
   * 次の行動を実行
   * @returns {{ done: boolean, action?: Object, waitingForInput?: boolean, currentUnit?: Object }}
   */
  executeNextAction() {
    if (this.state.status !== BATTLE_STATUS.ONGOING) {
      return { done: true, status: this.state.status };
    }

    const currentUnit = this.getCurrentUnit();

    // 死亡ユニットはスキップ
    if (!currentUnit || !currentUnit.isAlive) {
      return this.advanceTurn();
    }

    // 行動決定
    let action;
    if (this.state.isAuto || !currentUnit.isAlly) {
      // オートモードまたは敵：AIが決定
      action = this.ai.decideAction(currentUnit);
    } else {
      // 手動モード：外部から行動を受け取る
      return {
        done: false,
        waitingForInput: true,
        currentUnit,
        availableActions: this.actionExecutor.getAvailableActions(currentUnit),
        validTargets: this.actionExecutor.getValidTargets(currentUnit, 'attack'),
      };
    }

    // 行動実行
    return this.executeAction(currentUnit, action);
  }

  /**
   * 行動を実行（内部・外部共通）
   * @param {Object} unit - 行動ユニット
   * @param {Object} action - 行動
   * @returns {Object} 実行結果
   */
  executeAction(unit, action) {
    const result = this.actionExecutor.execute(unit, action);

    // ログ追加
    for (const log of result.logs) {
      this.addLog(log);
    }

    // 統計更新
    this.updateStats(result);

    // 撃破チェック
    this.checkDefeated();

    // ターン進行
    const advanceResult = this.advanceTurn();

    // アクション結果を返却に含める
    return {
      ...advanceResult,
      actionResult: result,
    };
  }

  /**
   * 手動行動を実行
   * @param {Object} action - 行動 { type, skillId?, targetId }
   * @returns {Object} 実行結果
   */
  executeManualAction(action) {
    const currentUnit = this.getCurrentUnit();
    if (!currentUnit || !currentUnit.isAlive) {
      return { done: false, error: '行動不能なユニットです' };
    }

    return this.executeAction(currentUnit, action);
  }

  /**
   * ターンを進行
   * @returns {Object} 進行結果
   */
  advanceTurn() {
    const { newIndex, turnEnded } = this.turnManager.advanceToNextUnit();
    this.state.currentTurnIndex = newIndex;

    // ターン終了処理
    if (turnEnded) {
      this.endTurn();
    }

    // 勝敗判定
    this.checkBattleEnd();

    const done = this.state.status !== BATTLE_STATUS.ONGOING;

    this.notifyChange();

    return {
      done,
      status: this.state.status,
      turnEnded,
      turnCount: this.state.turnCount,
    };
  }

  /**
   * ターン終了処理
   */
  endTurn() {
    // Phase 2: 全ユニットの状態異常・バフ・パッシブ処理
    this.processAllUnitsTurnEnd();

    this.turnManager.processTurnEnd();
    this.state.stats.turnsElapsed++;
    this.turnManager.startNewTurn();

    this.addLog({
      type: 'turn',
      message: `ターン ${this.state.turnCount} 開始`,
    });
  }

  // ============================================================
  // Phase 2: 状態異常・バフ・パッシブ処理
  // ============================================================

  /**
   * 戦闘開始時パッシブを処理
   */
  processBattleStartPassives() {
    const allUnits = [...this.state.allies, ...this.state.enemies];

    for (let i = 0; i < allUnits.length; i++) {
      const unit = allUnits[i];
      if (!unit.isAlive) continue;

      const { monster: updatedMonster, effects } = passiveManager.processBattleStart(unit);

      // ユニットを更新
      if (unit.isAlly) {
        this.state.allies[this.state.allies.findIndex(u => u.id === unit.id)] = updatedMonster;
      } else {
        this.state.enemies[this.state.enemies.findIndex(u => u.id === unit.id)] = updatedMonster;
      }

      // 効果をログに追加
      for (const effect of effects) {
        for (const msg of effect.messages || []) {
          this.addLog({ type: 'passive', message: msg });
        }
      }
    }
  }

  /**
   * 全ユニットのターン終了処理
   */
  processAllUnitsTurnEnd() {
    const allUnits = [...this.state.allies, ...this.state.enemies];

    for (let i = 0; i < allUnits.length; i++) {
      const unit = allUnits[i];
      if (!unit.isAlive) continue;

      // 状態異常のターン終了処理
      const statusResult = statusEffectEngine.processTurnEnd(unit);
      let updatedUnit = statusResult.monster;

      // 状態異常ダメージをログ
      for (const msg of statusResult.messages) {
        this.addLog({ type: 'status', message: msg });
      }

      // バフのターン終了処理
      const buffResult = buffManager.processTurnEnd(updatedUnit);
      updatedUnit = buffResult.monster;

      // 回復をログ
      for (const msg of buffResult.messages) {
        this.addLog({ type: 'buff', message: msg });
      }

      // パッシブのターン終了処理
      const passiveResult = passiveManager.processTurnEnd(updatedUnit);
      updatedUnit = passiveResult.monster;

      // パッシブ効果をログ
      for (const effect of passiveResult.effects) {
        for (const msg of effect.messages || []) {
          this.addLog({ type: 'passive', message: msg });
        }
      }

      // 無敵ターン減少
      if (updatedUnit.invincibleTurns > 0) {
        updatedUnit.invincibleTurns--;
        if (updatedUnit.invincibleTurns === 0) {
          this.addLog({ type: 'status', message: `${updatedUnit.name}の無敵が解除された` });
        }
      }

      // 死亡チェック
      if (updatedUnit.stats.currentHp <= 0) {
        updatedUnit.isAlive = false;
        updatedUnit.stats.currentHp = 0;
        this.addLog({ type: 'defeat', message: `${updatedUnit.name}は倒れた！` });
      }

      // ユニットを更新
      if (unit.isAlly) {
        const idx = this.state.allies.findIndex(u => u.id === unit.id);
        if (idx >= 0) this.state.allies[idx] = updatedUnit;
      } else {
        const idx = this.state.enemies.findIndex(u => u.id === unit.id);
        if (idx >= 0) this.state.enemies[idx] = updatedUnit;
      }
    }
  }

  /**
   * 行動可能かチェック（状態異常考慮）
   * @param {Object} unit - ユニット
   * @returns {{ canAct: boolean, reason: string | null }}
   */
  checkCanAct(unit) {
    return statusEffectEngine.canAct(unit);
  }

  /**
   * スキル使用可能かチェック（状態異常考慮）
   * @param {Object} unit - ユニット
   * @returns {{ canUseSkill: boolean, reason: string | null }}
   */
  checkCanUseSkill(unit) {
    return statusEffectEngine.canUseSkill(unit);
  }

  /**
   * ダメージ計算時の補正を取得
   * @param {Object} attacker - 攻撃ユニット
   * @param {Object} defender - 防御ユニット
   * @param {Object} context - コンテキスト
   * @returns {{ atkMultiplier, dmgDealtMultiplier, dmgReceivedMultiplier, defModifier, spdModifier }}
   */
  getDamageModifiers(attacker, defender, context = {}) {
    // 攻撃側補正
    const statusAtkMult = statusEffectEngine.getAttackMultiplier(attacker);
    const buffAtkMult = buffManager.getAttackMultiplier(attacker);
    const statusDmgDealtMult = statusEffectEngine.getDamageDealtMultiplier(attacker);
    const passiveDmgDealtMult = passiveManager.getDamageDealtMultiplier(attacker, context);

    // 防御側補正
    const statusDmgReceivedMult = statusEffectEngine.getDamageReceivedMultiplier(defender);
    const passiveDmgReceivedMult = passiveManager.getDamageReceivedMultiplier(defender, context);

    // 防御補正
    const statusDefMod = statusEffectEngine.getDefenseModifier(defender);
    const buffDefMod = buffManager.getDefenseModifier(defender);

    // 速度補正
    const statusSpdMod = statusEffectEngine.getSpeedModifier(attacker);
    const buffSpdMod = buffManager.getSpeedModifier(attacker);

    return {
      atkMultiplier: statusAtkMult * buffAtkMult,
      dmgDealtMultiplier: statusDmgDealtMult * passiveDmgDealtMult,
      dmgReceivedMultiplier: statusDmgReceivedMult * passiveDmgReceivedMult,
      defModifier: statusDefMod + buffDefMod,
      spdModifier: statusSpdMod + buffSpdMod,
    };
  }

  /**
   * クリティカル率を計算
   * @param {Object} attacker - 攻撃ユニット
   * @param {Object} defender - 防御ユニット
   * @param {Object} context - コンテキスト
   * @returns {number} クリティカル率
   */
  calculateCritRate(attacker, defender, context = {}) {
    let critRate = attacker.stats.critRate || 0.05;

    // バフ補正
    critRate += buffManager.getCritRateModifier(attacker);

    // パッシブ補正（確定クリティカル等）
    const passives = passiveManager.getTriggeredPassives(attacker, 'ON_ACTION', context);
    for (const passive of passives) {
      if (passive.effect === 'CRIT_RATE' && passive.condition) {
        // HP条件チェック（例：HP30%以下で確定クリ）
        if (passive.condition.type === 'HP_BELOW' && passive.condition.target === 'enemy') {
          const defHpPercent = defender.stats.currentHp / defender.stats.maxHp;
          if (defHpPercent <= passive.condition.value) {
            critRate = passive.value; // 確定クリティカル
          }
        }
      }
    }

    return Math.min(1.0, critRate);
  }

  /**
   * 回避判定
   * @param {Object} defender - 防御ユニット
   * @returns {boolean} 回避成功
   */
  checkEvasion(defender) {
    const evasionRate = buffManager.getEvasionRate(defender);
    return Math.random() < evasionRate;
  }

  /**
   * ダメージ適用（シールド・根性・無敵考慮）
   * @param {Object} unit - ユニット
   * @param {number} damage - ダメージ量
   * @returns {{ finalDamage, absorbed, survived, message }}
   */
  applyDamage(unit, damage) {
    let finalDamage = damage;
    let absorbed = 0;
    let survived = false;
    const messages = [];

    // 無敵チェック
    if (unit.invincibleTurns > 0) {
      messages.push(`${unit.name}は無敵状態！ダメージを受けない！`);
      return { finalDamage: 0, absorbed: damage, survived: false, messages };
    }

    // ダメージ上限チェック（パッシブ）
    finalDamage = passiveManager.applyDamageCap(unit, finalDamage);

    // シールドで吸収
    if (unit.shield > 0) {
      if (unit.shield >= finalDamage) {
        unit.shield -= finalDamage;
        absorbed = finalDamage;
        finalDamage = 0;
        messages.push(`シールドで${absorbed}ダメージを吸収！`);
      } else {
        absorbed = unit.shield;
        finalDamage -= unit.shield;
        unit.shield = 0;
        messages.push(`シールドで${absorbed}ダメージを吸収！シールド破壊！`);
      }
    }

    // HP減少
    const previousHp = unit.stats.currentHp;
    unit.stats.currentHp = Math.max(0, unit.stats.currentHp - finalDamage);

    // 根性チェック
    if (unit.stats.currentHp === 0 && unit.hasGuts && previousHp > 0) {
      unit.stats.currentHp = 1;
      unit.hasGuts = false;
      survived = true;
      messages.push(`${unit.name}は根性で耐えた！`);
    }

    // 凍結解除チェック
    if (finalDamage > 0) {
      const { monster: updatedUnit, messages: statusMsgs } = statusEffectEngine.onDamageReceived(unit);
      Object.assign(unit, updatedUnit);
      messages.push(...statusMsgs);
    }

    return { finalDamage, absorbed, survived, messages };
  }

  // ============================================================
  // 判定
  // ============================================================

  /**
   * 撃破判定
   */
  checkDefeated() {
    const allUnits = [...this.state.allies, ...this.state.enemies];
    for (const unit of allUnits) {
      if (unit.stats.currentHp <= 0 && unit.isAlive) {
        unit.isAlive = false;
        unit.stats.currentHp = 0;
      }
    }
  }

  /**
   * 勝敗判定
   */
  checkBattleEnd() {
    const alliesAlive = this.state.allies.some(u => u.isAlive);
    const enemiesAlive = this.state.enemies.some(u => u.isAlive);

    if (!enemiesAlive) {
      this.state.status = BATTLE_STATUS.VICTORY;
      this.addLog({ type: 'victory', message: '戦闘に勝利した！' });
    } else if (!alliesAlive) {
      this.state.status = BATTLE_STATUS.DEFEAT;
      this.addLog({ type: 'defeat', message: '全滅した...' });
    }
  }

  // ============================================================
  // ユーティリティ
  // ============================================================

  /**
   * 現在のユニットを取得
   * @returns {Object | null}
   */
  getCurrentUnit() {
    return this.turnManager.getCurrentUnit();
  }

  /**
   * ログを追加
   * @param {Object} entry - ログエントリ
   */
  addLog(entry) {
    this.state.logs.push({
      turn: this.state.turnCount,
      timestamp: Date.now(),
      ...entry,
    });
  }

  /**
   * 統計を更新
   * @param {Object} result - 行動結果
   */
  updateStats(result) {
    if (!result.results) return;

    for (const r of result.results) {
      if (r.damage) {
        // 味方が与えたダメージ
        const actor = this.findUnitById(result.actorId);
        if (actor?.isAlly) {
          this.state.stats.totalDamageDealt += r.damage;
        } else {
          this.state.stats.totalDamageReceived += r.damage;
        }
      }

      if (r.isCritical) {
        this.state.stats.criticalHits++;
      }
    }

    if (result.type === 'skill') {
      this.state.stats.skillsUsed++;
    }
  }

  /**
   * IDでユニットを検索
   * @param {string} unitId - ユニットID
   * @returns {Object | null}
   */
  findUnitById(unitId) {
    const allUnits = [...this.state.allies, ...this.state.enemies];
    return allUnits.find(u => u.id === unitId) || null;
  }

  /**
   * 変更通知
   */
  notifyChange() {
    if (this.onChange) {
      this.onChange(this.getState());
    }
  }

  // ============================================================
  // 外部API
  // ============================================================

  /**
   * 現在の状態を取得
   * @returns {Object} バトル状態のコピー
   */
  getState() {
    return {
      ...this.state,
      allies: this.state.allies.map(u => ({
        ...u,
        stats: { ...u.stats },
        statusEffects: [...(u.statusEffects || [])],
        buffs: [...(u.buffs || [])],
      })),
      enemies: this.state.enemies.map(u => ({
        ...u,
        stats: { ...u.stats },
        statusEffects: [...(u.statusEffects || [])],
        buffs: [...(u.buffs || [])],
      })),
      turnOrder: [...this.state.turnOrder],
      logs: [...this.state.logs],
      stats: { ...this.state.stats },
    };
  }

  // ============================================================
  // Phase 2: 状態異常・バフ適用API
  // ============================================================

  /**
   * 状態異常を付与
   * @param {string} unitId - ユニットID
   * @param {string} statusType - 状態異常タイプ
   * @param {Object} options - オプション
   * @returns {{ success: boolean, message: string }}
   */
  applyStatusEffect(unitId, statusType, options = {}) {
    const unit = this.findUnitById(unitId);
    if (!unit || !unit.isAlive) {
      return { success: false, message: 'ユニットが見つかりません' };
    }

    // 状態異常耐性チェック（パッシブ）
    if (passiveManager.hasStatusImmunity(unit, statusType)) {
      this.addLog({ type: 'resist', message: `${unit.name}は${statusType}を無効化した！` });
      return { success: false, message: '状態異常無効' };
    }

    const result = statusEffectEngine.applyStatus(unit, statusType, options);

    // 相殺チェック（バフとの相殺）
    if (result.success) {
      const cancelResult = buffManager.checkDebuffCancellation(result.monster, statusType, options.stack || 1);
      if (cancelResult.message) {
        this.addLog({ type: 'cancel', message: cancelResult.message });
      }
      Object.assign(unit, cancelResult.monster);
    } else {
      Object.assign(unit, result.monster);
    }

    if (result.message) {
      this.addLog({ type: 'status', message: result.message });
    }

    this.notifyChange();
    return { success: result.success, message: result.message };
  }

  /**
   * バフを付与
   * @param {string} unitId - ユニットID
   * @param {string} buffType - バフタイプ
   * @param {Object} options - オプション
   * @returns {{ success: boolean, message: string }}
   */
  applyBuff(unitId, buffType, options = {}) {
    const unit = this.findUnitById(unitId);
    if (!unit || !unit.isAlive) {
      return { success: false, message: 'ユニットが見つかりません' };
    }

    const result = buffManager.applyBuff(unit, buffType, options);
    Object.assign(unit, result.monster);

    if (result.message) {
      this.addLog({ type: 'buff', message: result.message });
    }

    if (result.canceled) {
      this.addLog({ type: 'cancel', message: `バフと状態異常が相殺された` });
    }

    this.notifyChange();
    return { success: result.success, message: result.message };
  }

  /**
   * 回復を適用（呪い・回復強化パッシブ考慮）
   * @param {string} unitId - ユニットID
   * @param {number} baseHeal - 基礎回復量
   * @returns {{ actualHeal: number, messages: string[] }}
   */
  applyHeal(unitId, baseHeal) {
    const unit = this.findUnitById(unitId);
    if (!unit || !unit.isAlive) {
      return { actualHeal: 0, messages: [] };
    }

    const messages = [];

    // 呪い補正（状態異常）
    const statusMult = statusEffectEngine.getHealMultiplier(unit);
    // パッシブ補正
    const passiveMult = passiveManager.getHealMultiplier(unit);

    const actualHeal = Math.floor(baseHeal * statusMult * passiveMult);

    const previousHp = unit.stats.currentHp;
    unit.stats.currentHp = Math.min(unit.stats.maxHp, unit.stats.currentHp + actualHeal);
    const healedAmount = unit.stats.currentHp - previousHp;

    messages.push(`${unit.name}は${healedAmount}回復した！`);

    if (statusMult < 1) {
      messages.push(`呪いで回復効果が減少している！`);
    }

    // 毒解除チェック
    const { monster: updatedUnit, messages: statusMsgs } = statusEffectEngine.onHealReceived(unit);
    Object.assign(unit, updatedUnit);
    messages.push(...statusMsgs);

    this.notifyChange();
    return { actualHeal: healedAmount, messages };
  }

  /**
   * オートモード切り替え
   * @param {boolean} isAuto - オートモードフラグ
   */
  setAutoMode(isAuto) {
    this.state.isAuto = isAuto;
    this.notifyChange();
  }

  /**
   * 速度変更
   * @param {number} speed - 速度（1, 2, 3）
   */
  setSpeed(speed) {
    this.state.speed = Math.max(1, Math.min(3, speed));
    this.notifyChange();
  }

  /**
   * 逃走
   * @returns {{ success: boolean }}
   */
  escape() {
    // ボス戦は逃走不可
    if (this.state.battleType === 'boss') {
      this.addLog({ type: 'escape_fail', message: 'ボス戦からは逃げられない！' });
      return { success: false };
    }

    // 逃走成功率（通常70%、強敵50%）
    const escapeRate = this.state.battleType === 'strong' ? 0.5 : 0.7;
    const success = Math.random() < escapeRate;

    if (success) {
      this.state.status = BATTLE_STATUS.ESCAPED;
      this.addLog({ type: 'escape', message: '逃走に成功した！' });
    } else {
      this.addLog({ type: 'escape_fail', message: '逃走に失敗した！' });
    }

    this.notifyChange();
    return { success };
  }

  /**
   * バトル結果を取得
   * @returns {Object} 結果情報
   */
  getBattleResult() {
    return {
      battleId: this.state.battleId,
      status: this.state.status,
      isVictory: this.state.status === BATTLE_STATUS.VICTORY,
      isDefeat: this.state.status === BATTLE_STATUS.DEFEAT,
      isEscaped: this.state.status === BATTLE_STATUS.ESCAPED,
      stats: { ...this.state.stats },
      survivingAllies: this.state.allies.filter(u => u.isAlive).length,
      turnCount: this.state.turnCount,
      battleType: this.state.battleType,
      // 味方の最終HP状態を含める（ダンジョン継続のため）
      allyHpStatus: this.state.allies.map(ally => ({
        instanceId: ally.instanceId,
        currentHp: ally.stats.currentHp,
        maxHp: ally.stats.maxHp,
        isAlive: ally.isAlive,
      })),
    };
  }
}
